/**
 * Shared validation logic for the Employee Create / Edit forms.
 * Extracted so both pages enforce the exact same rules.
 *
 * Returns an object whose keys are the offending field paths (e.g. "first_name",
 * "national_id") and whose values are localized error messages. Empty object = valid.
 */
import { PATTERNS } from '@/constants/employeeForm';
import { isSaudi } from '@/constants/nationalities';

export function validateEmployeeForm(formData, { isRTL = false, mode = 'create' } = {}) {
  const errors = {};
  const data = formData || {};
  const isEdit = mode === 'edit';

  // Names (English OR Arabic)
  if (!data.first_name && !data.first_name_ar) {
    errors.first_name = isRTL
      ? 'الاسم الأول بالإنجليزية أو العربية مطلوب'
      : 'First name (English or Arabic) is required';
  }
  if (!data.last_name && !data.last_name_ar) {
    errors.last_name = isRTL
      ? 'اسم العائلة بالإنجليزية أو العربية مطلوب'
      : 'Last name (English or Arabic) is required';
  }

  // Required: nationality, hire date, department, job title
  if (!data.nationality) {
    errors.nationality = isRTL ? 'الجنسية مطلوبة' : 'Nationality is required';
  }
  if (!data.hire_date) {
    errors.hire_date = isRTL ? 'تاريخ التعيين مطلوب' : 'Hire date is required';
  }
  if (!data.department) {
    errors.department = isRTL ? 'القسم مطلوب' : 'Department is required';
  }
  if (!data.job_title && !data.job_title_ar) {
    errors.job_title = isRTL ? 'المسمى الوظيفي مطلوب' : 'Job title is required';
  }

  // Daily working hours — must be 8 or 10 (Phase 22). Create-only required check;
  // Edit allows pre-existing values to remain untouched.
  if (!isEdit) {
    if (!data.daily_working_hours) {
      errors.daily_working_hours = isRTL
        ? 'ساعات العمل اليومية مطلوبة'
        : 'Daily Working Hours is required';
    } else if (!['8', '10', 8, 10].includes(data.daily_working_hours)) {
      errors.daily_working_hours = isRTL
        ? 'ساعات العمل اليومية يجب أن تكون 8 أو 10'
        : 'Daily Working Hours must be 8 or 10';
    }
  }

  // Mobile (KSA)
  if (!data.phone) {
    errors.phone = isRTL ? 'رقم الجوال مطلوب' : 'Mobile number is required';
  } else if (!PATTERNS.saudiMobile.test(String(data.phone).replace(/\s/g, ''))) {
    errors.phone = isRTL ? 'رقم جوال سعودي غير صالح' : 'Invalid KSA mobile number format';
  }

  // KSA Identity:
  //   - On create: NID required for Saudis, Iqama required for non-Saudis.
  //   - On edit:   identity fields are masked & only re-validated when the user
  //                actually types a replacement value (format check only).
  if (isEdit) {
    if (data.national_id && !PATTERNS.saudiNationalId.test(data.national_id)) {
      errors.national_id = isRTL
        ? 'الهوية الوطنية يجب أن تبدأ بـ 1 وتكون 10 أرقام'
        : 'National ID must start with 1 and be 10 digits';
    }
    if (data.iqama && !PATTERNS.iqama.test(data.iqama)) {
      errors.iqama = isRTL
        ? 'الإقامة يجب أن تبدأ بـ 2 وتكون 10 أرقام'
        : 'Iqama must start with 2 and be 10 digits';
    }
  } else if (isSaudi(data.nationality)) {
    if (!data.national_id) {
      errors.national_id = isRTL
        ? 'الهوية الوطنية مطلوبة للسعوديين'
        : 'National ID is required for Saudi nationals';
    } else if (!PATTERNS.saudiNationalId.test(data.national_id)) {
      errors.national_id = isRTL
        ? 'الهوية الوطنية يجب أن تبدأ بـ 1 وتكون 10 أرقام'
        : 'National ID must start with 1 and be 10 digits';
    }
  } else if (data.nationality && !isSaudi(data.nationality)) {
    if (!data.iqama) {
      errors.iqama = isRTL
        ? 'الإقامة مطلوبة لغير السعوديين'
        : 'Iqama is required for non-Saudi nationals';
    } else if (!PATTERNS.iqama.test(data.iqama)) {
      errors.iqama = isRTL
        ? 'الإقامة يجب أن تبدأ بـ 2 وتكون 10 أرقام'
        : 'Iqama must start with 2 and be 10 digits';
    }
  }

  // Optional email
  if (data.personal_email && !PATTERNS.email.test(data.personal_email)) {
    errors.personal_email = isRTL
      ? 'صيغة البريد الإلكتروني غير صالحة'
      : 'Invalid email format';
  }

  // Optional IBAN (Saudi format)
  if (data.iban) {
    const cleanIban = String(data.iban).replace(/\s/g, '').toUpperCase();
    if (!cleanIban.startsWith('SA') || cleanIban.length !== 24) {
      errors.iban = isRTL
        ? 'الآيبان السعودي يجب أن يبدأ بـ SA ويكون 24 حرف'
        : 'Saudi IBAN must start with SA and be 24 characters';
    }
  }

  // Annual leave (2026-09-17) — mirrors the backend rule exactly: a whole
  // number of days, anything outside 21/30/90 has to be justified in writing,
  // and nobody rejoins before the leave they are rejoining from started.
  if (data.annual_leave_days !== '' && data.annual_leave_days !== null && data.annual_leave_days !== undefined) {
    const annualDays = Number(data.annual_leave_days);
    if (!Number.isInteger(annualDays) || annualDays < 1 || annualDays > 365) {
      errors.annual_leave_days = isRTL
        ? 'أيام الإجازة السنوية يجب أن تكون رقماً صحيحاً بين 1 و 365'
        : 'Annual leave days must be a whole number between 1 and 365';
    } else if (![21, 30, 90].includes(annualDays)) {
      const justification = String(data.annual_leave_days_justification || '').trim();
      if (justification.length < 10) {
        errors.annual_leave_days_justification = isRTL
          ? `استحقاق ${annualDays} يوماً يحتاج مبرراً مكتوباً لا يقل عن 10 أحرف`
          : `A ${annualDays}-day entitlement requires a written justification of at least 10 characters`;
      }
    }
  }

  if (data.last_annual_leave_start_date && data.last_annual_leave_rejoin_date
      && data.last_annual_leave_rejoin_date < data.last_annual_leave_start_date) {
    errors.last_annual_leave_rejoin_date = isRTL
      ? 'تاريخ المباشرة لا يمكن أن يكون قبل بداية الإجازة'
      : 'The rejoin date cannot be before the annual leave start date';
  }

  return errors;
}

/**
 * Map a field name to the tab it lives on, so failed validation can auto-jump
 * the user to the first tab containing an error.
 */
export const FIELD_TO_TAB = {
  first_name: 'personal',
  first_name_ar: 'personal',
  last_name: 'personal',
  last_name_ar: 'personal',
  nationality: 'personal',
  phone: 'personal',
  personal_email: 'personal',
  national_id: 'identity',
  iqama: 'identity',
  hire_date: 'employment',
  department: 'employment',
  job_title: 'employment',
  daily_working_hours: 'employment',
  annual_leave_days: 'employment',
  annual_leave_days_justification: 'employment',
  last_annual_leave_rejoin_date: 'employment',
  iban: 'bank',
};

export function firstTabWithError(errors) {
  for (const field of Object.keys(errors || {})) {
    if (FIELD_TO_TAB[field]) return FIELD_TO_TAB[field];
  }
  return null;
}
